/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, type ReactNode, useCallback, useEffect, useRef } from 'react';

import { css, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/custom-theme-button/custom-theme-button';
import { SplitButton } from '@atlaskit/button/split-button/split-button';
import AddIcon from '@atlaskit/icon/core/add';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import CrossIcon from '@atlaskit/icon/core/cross';
import { TopNavButton, TopNavIconButton } from '@atlaskit/navigation-system/theming/top-nav-button';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightTarget from '@atlaskit/onboarding/spotlight-target';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { Pulse as SpotlightPulse } from '@atlaskit/onboarding/target';
import { fg } from '@atlaskit/platform-feature-flags/fg';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { Hide } from '@atlaskit/primitives/responsive/hide';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { Show } from '@atlaskit/primitives/responsive/show';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { UNSAFE_useMediaQuery as useMediaQuery } from '@atlaskit/primitives/responsive/use-media-query';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { RESPONSIVE_BREAKPOINT, TRIGGER_BUTTON_TEST_ID } from '../../../constants';
import { useUniversalCreateState } from '../../../context';
import {
	type ButtonPart,
	useUniversalCreateAnalytics,
} from '../../../hooks/use-universal-create-analytics';
import { withIntlProvider } from '../../../services/intl-provider/intlProvider';
import type { UniversalCreateButtonProps } from '../../../types/button';
import type { MenuItem, TriggerPropsWithSplitPrimaryRef } from '../../../types/menu-base';

const triggerButton = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// below 768px https://staging.atlassian.design/components/primitives/responsive/breakpoints/examples#media-queries
		// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles
		'@media not all and (min-width: 48rem)': {
			display: 'block',

			// to match with the icon button spacing
			paddingLeft: token('space.100'),
			paddingRight: token('space.100'),
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
			'> span > span': {
				// enforce icon has no extra padding
				paddingLeft: 0,
				paddingRight: 0,
			},
		},
	},
});

/**
 * Clean up nav3 button once Confluence has cleaned up nav3
 * https://jplat.atlassian.net/browse/BLU-7401
 */
const nav3ButtonTextStyles = css({
	// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'@media not all and (min-width: 48rem)': {
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
			'&:hover': {
				paddingLeft: token('space.100'),
				paddingRight: token('space.100'),
			},
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			'span:nth-of-type(1)': {
				marginLeft: 0,
				marginRight: 0,
			},
			// eslint-disable-next-line  @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			'span:nth-of-type(2)': {
				display: 'none',
			},
		},
	},
});

const messages = defineMessages({
	secondaryButtonTooltipText: {
		id: 'platform.navigation-experiences.universal-create.secondary-tooltip-text',
		defaultMessage: 'More create options',
		description:
			'Tooltip text shown when hovering over the secondary button in the split create button that opens additional create options',
	},
});

function TooltipWrapper({
	children,
	text,
	isScreenReaderAnnouncementDisabled = true,
}: {
	children: ReactNode;
	isScreenReaderAnnouncementDisabled?: boolean;
	text: ReactNode;
}) {
	return (
		<Tooltip
			content={text}
			tag="span"
			isScreenReaderAnnouncementDisabled={isScreenReaderAnnouncementDisabled}
			position="bottom"
			ignoreTooltipPointerEvents
		>
			{children}
		</Tooltip>
	);
}

type TriggerButtonPropsBase = {
	buttonTheme?: UniversalCreateButtonProps['buttonTheme'];
	children: string;
	/**
	 * @deprecated {@link https://hello.jira.atlassian.cloud/browse/ENGHEALTH-52105 @atlaskit/onboarding is deprecated in favor of @atlaskit/spotlight}
	 */
	enableTriggerPulse?: boolean;
	isEntryPointEnabled?: boolean;
	isNav4Enabled?: boolean;
	onClick: () => void;
	onMouseEnter?: UniversalCreateButtonProps['onMouseEnter'];
	onMouseLeave?: UniversalCreateButtonProps['onMouseLeave'];
	testId?: string;
	/**
	 * @deprecated {@link https://hello.jira.atlassian.cloud/browse/ENGHEALTH-52105 @atlaskit/onboarding is deprecated in favor of @atlaskit/spotlight}
	 */
	triggerButtonSpotlightTarget?: string;
};

export type TriggerButtonProps = TriggerButtonPropsBase &
	(
		| {
				buttonOnly: true;
				isOpen?: boolean;
				primaryActionItem?: never;
				triggerProps?: TriggerPropsWithSplitPrimaryRef;
		  }
		| {
				buttonOnly?: false;
				isOpen: boolean;
				primaryActionItem?: MenuItem;
				triggerProps: TriggerPropsWithSplitPrimaryRef;
		  }
	);
function TriggerButtonInner({
	triggerProps,
	testId,
	onClick,
	children,
	isOpen = false,
	triggerButtonSpotlightTarget,
	enableTriggerPulse,
	isNav4Enabled = true,
	buttonTheme,
	onMouseEnter,
	onMouseLeave,
	primaryActionItem,
	isEntryPointEnabled = false,
	buttonOnly = false,
}: TriggerButtonProps) {
	const isButtonOnly = buttonOnly;
	const { product, sections } = useUniversalCreateState();
	const { fireCreateButtonClicked, fireCreateButtonHovered, fireCreateButtonViewed } =
		useUniversalCreateAnalytics();
	const { formatMessage } = useIntl();
	const isDesktop = useMediaQuery('above.sm')?.matches ?? false;
	const version = sections?.length ? 'v2' : 'v1';
	const hasHoveredRef = useRef(false);

	const isSplitButton = Boolean(primaryActionItem);
	const buttonVariant = isSplitButton ? 'split' : 'standard';

	const hasFiredViewEvent = useRef(false);

	useEffect(
		function fireButtonViewEvent() {
			if (hasFiredViewEvent.current) {
				return;
			}
			hasFiredViewEvent.current = true;
			fireCreateButtonViewed({ buttonVariant, version, isButtonOnly });
		},
		[fireCreateButtonViewed, buttonVariant, version, isButtonOnly],
	);

	// Extract splitPrimaryRef from triggerProps for split button variant
	const { splitButtonPrimaryRef, ...restTriggerProps } = triggerProps ?? {};

	const onButtonClick = useCallback(() => {
		/// For entrypoint path (isEntryPointEnabled is true), set to expanded when isOpen is true
		// For non-entrypoint path (isEntryPointEnabled is false), set to expanded when isOpen is false (current state), as in the legacy mode, the event is fired before menu opens.
		const itemState = isButtonOnly
			? 'button-only'
			: isEntryPointEnabled === isOpen
				? 'expanded'
				: 'collapsed';
		const clickedButtonPart = isSplitButton ? 'dropdown' : 'full';
		const clickedButtonVariant = isSplitButton ? 'split' : 'standard';
		fireCreateButtonClicked({
			itemState,
			buttonPart: clickedButtonPart,
			buttonVariant: clickedButtonVariant,
		});
		onClick();
	}, [fireCreateButtonClicked, isOpen, onClick, isEntryPointEnabled, isButtonOnly, isSplitButton]);

	const primaryItemOnClick = primaryActionItem?.onClick;
	const onPrimaryButtonClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			const itemState = isEntryPointEnabled === isOpen ? 'expanded' : 'collapsed';
			fireCreateButtonClicked({ itemState, buttonPart: 'primary', buttonVariant: 'split' });
			primaryItemOnClick?.(event);
		},
		[fireCreateButtonClicked, isEntryPointEnabled, isOpen, primaryItemOnClick],
	);
	const CreateIcon = isButtonOnly ? AddIcon : isOpen ? CrossIcon : AddIcon;

	interface CreateEnterEventOptions {
		buttonPart: ButtonPart;
		shouldTriggerMouseEnterProp?: boolean;
	}

	const createEnterEvent = useCallback(
		({ buttonPart, shouldTriggerMouseEnterProp = true }: CreateEnterEventOptions) =>
			(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
				if (!hasHoveredRef.current) {
					hasHoveredRef.current = true;
					const itemState = isButtonOnly
						? 'button-only'
						: isEntryPointEnabled === isOpen
							? 'expanded'
							: 'collapsed';
					fireCreateButtonHovered({
						buttonPart,
						buttonVariant,
						itemState,
						version,
					});
				}
				if (shouldTriggerMouseEnterProp) {
					onMouseEnter?.(event);
				}
			},
		[
			onMouseEnter,
			fireCreateButtonHovered,
			isOpen,
			isEntryPointEnabled,
			version,
			buttonVariant,
			isButtonOnly,
		],
	);

	const enterEvent = createEnterEvent({ buttonPart: 'full' });
	const enterEventPrimary = createEnterEvent({ buttonPart: 'primary' });
	const enterEventDropdown = createEnterEvent({ buttonPart: 'dropdown' });

	const leaveEvent = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onMouseLeave?.(event);
		},
		[onMouseLeave],
	);

	const nav3Button = !isNav4Enabled ? (
		<div css={triggerButton}>
			{product === 'confluence' ? (
				<div css={nav3ButtonTextStyles}>
					<Button
						{...restTriggerProps}
						appearance="primary"
						// eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis, @atlaskit/design-system/no-unsafe-style-overrides
						theme={buttonTheme}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						onClick={onButtonClick}
						iconBefore={<CreateIcon label="" data-vc="global-content-create-button" />}
						testId={testId || TRIGGER_BUTTON_TEST_ID}
						aria-label={children}
						isSelected={isOpen}
					>
						<Show above="sm" as="span">
							{children}
						</Show>
					</Button>
				</div>
			) : (
				<TooltipWrapper text={children}>
					<TopNavButton
						{...restTriggerProps}
						appearance="primary"
						onClick={onButtonClick}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						isSelected={isOpen}
						iconBefore={CreateIcon}
						testId={testId || TRIGGER_BUTTON_TEST_ID}
						aria-label={children}
					>
						<Show above="sm" as="span">
							{children}
						</Show>
					</TopNavButton>
				</TooltipWrapper>
			)}
		</div>
	) : null;

	const responsiveSafeButton = isNav4Enabled ? (
		<Fragment>
			<Show above={RESPONSIVE_BREAKPOINT} as="span">
				<TooltipWrapper text={children}>
					<TopNavButton
						{...(isDesktop ? restTriggerProps : {})}
						appearance="primary"
						onClick={onButtonClick}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						isSelected={isOpen}
						iconBefore={CreateIcon}
						testId={testId || TRIGGER_BUTTON_TEST_ID}
						aria-label={children}
						data-responsive-variant="desktop"
					>
						{children}
					</TopNavButton>
				</TooltipWrapper>
			</Show>
			<Hide above={RESPONSIVE_BREAKPOINT} as="span">
				<TooltipWrapper text={children}>
					<TopNavIconButton
						{...(!isDesktop ? restTriggerProps : {})}
						appearance="primary"
						onClick={onButtonClick}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						isSelected={isOpen}
						icon={CreateIcon}
						testId={`${testId || TRIGGER_BUTTON_TEST_ID}-mobile`}
						label={children}
						data-responsive-variant="mobile"
					/>
				</TooltipWrapper>
			</Hide>
		</Fragment>
	) : null;

	const splitButtonVariant = primaryActionItem ? (
		<Fragment>
			<Show above={RESPONSIVE_BREAKPOINT}>
				<SplitButton appearance="primary">
					<TooltipWrapper text={children}>
						<TopNavButton
							ref={splitButtonPrimaryRef}
							appearance="primary"
							onClick={onPrimaryButtonClick}
							onMouseEnter={enterEventPrimary}
							aria-label={children}
							testId="uc-trigger-button-primary"
						>
							{children}
						</TopNavButton>
					</TooltipWrapper>
					<TooltipWrapper
						text={formatMessage(messages.secondaryButtonTooltipText)}
						isScreenReaderAnnouncementDisabled={false}
					>
						<TopNavIconButton
							{...(isDesktop ? restTriggerProps : {})}
							appearance="primary"
							onClick={onButtonClick}
							isSelected={isOpen}
							icon={ChevronDownIcon}
							data-responsive-variant="desktop"
							label={children}
							testId={`${testId || TRIGGER_BUTTON_TEST_ID}-split`}
							onMouseEnter={enterEventDropdown}
							onMouseLeave={leaveEvent}
						/>
					</TooltipWrapper>
				</SplitButton>
			</Show>
			<Hide above="sm">
				<TooltipWrapper text={children}>
					<TopNavIconButton
						{...(!isDesktop ? restTriggerProps : {})}
						appearance="primary"
						onClick={onButtonClick}
						onMouseEnter={enterEvent}
						onMouseLeave={leaveEvent}
						isSelected={isOpen}
						icon={CreateIcon}
						testId={`${testId || TRIGGER_BUTTON_TEST_ID}-mobile`}
						label={children}
						data-responsive-variant="mobile"
					/>
				</TooltipWrapper>
			</Hide>
		</Fragment>
	) : null;

	const buttonToRender = primaryActionItem
		? splitButtonVariant
		: isNav4Enabled
			? responsiveSafeButton
			: nav3Button;

	if (!fg('navx_uc_spotlight_upgrade') && triggerButtonSpotlightTarget) {
		return (
			<SpotlightTarget name={triggerButtonSpotlightTarget}>
				<SpotlightPulse radius={3} pulse={enableTriggerPulse}>
					{buttonToRender}
				</SpotlightPulse>
			</SpotlightTarget>
		);
	}

	return buttonToRender;
}

export const TriggerButton: (props: TriggerButtonProps) => JSX.Element =
	withIntlProvider(TriggerButtonInner);
